<script setup lang="tsx">
import { UseClipboard } from '@vueuse/components';

import { jwtDecode } from 'jwt-decode';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { useOrgs } from '@/src/core/admin/orgs';

import { supabase } from '../../supabase';
import { getMessage } from '../../utils/exception';

const props = defineProps({
  dicomArchive: {
    type: String,
  },
});

const emit = defineEmits<{
  error: [message: string];
}>();

const dialog = ref(false);
const dialogValid = ref<boolean | null>(null);
const editedIndex = ref<number | null>(null);

const dialogCreated = ref(false);
const dialogDelete = ref(false);

interface Row {
  id: string;
  org: string;
  token: string;
  payload: any;
}

interface Data {
  id: string | null;
  name: string;
  org: string;
  validitySeconds: number;
}

const defaultItem = ref<Data>({
  id: null,
  name: '',
  org: '',
  validitySeconds: 365 * 24 * 3600,
});
const editedItem = ref<Data>();

const dialogTitle = computed(() => {
  return editedIndex.value === null ? 'New Token' : 'Edit Token';
});

const busy = ref(true);

const itemsPerPageOptions = [5, -1];

const items = ref<Row[]>([]);

const headers = ref([
  {
    title: 'Name',
    align: 'start' as const,
    sortable: true,
    key: 'payload.sub',
  },
  { title: 'Expiry', key: 'payload.exp' },
  { title: 'Token', key: 'token', maxWidth: '20em', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false },
]);

const validityAutocompleteItems = [
  {
    title: '90 days',
    value: 90 * 24 * 3600,
  },
  {
    title: '180 days',
    value: 180 * 24 * 3600,
  },
  {
    title: '1 year',
    value: 365 * 24 * 3600,
  },
  {
    title: '2 years',
    value: 2 * 365 * 24 * 3600,
  },
  {
    title: '10 years',
    value: 10 * 365 * 24 * 3600,
  },
];

const revealedTokens = ref([] as string[]);
const createdToken = ref({
  name: '',
  value: '',
});

const rules = {
  required: (value: string) => !!value || 'Field is required',
};

async function loadTokens() {
  try {
    busy.value = true;
    const { data, error } = await supabase.from('token').select('*');
    busy.value = false;
    if (error) throw error;
    const rows = data.map(
      r =>
        (({
          ...r,
          payload: jwtDecode(r.token),
        }) as Row),
    );
    items.value = rows.filter(
      r =>
        r.payload.type === 'stow-rs' && r.payload.kid === props.dicomArchive,
    );
    console.log(data);
  }
  catch (error) {
    emit('error', getMessage(error));
  }
}

interface PromiseRejectedResult {
  status: 'rejected';
  reason: any;
}

function editedItemFromRow(item: Row): Data {
  return {
    id: item.id,
    org: item.org,
    name: item.payload.sub,
    validitySeconds: item.payload.exp - Date.now() / 1000,
  };
}

function resetEditedItem() {
  editedIndex.value = null;
  editedItem.value = Object.assign({} as Data, defaultItem.value);
}

const { orgAutocompleteItems, loadOrgs } = useOrgs();

async function load() {
  busy.value = true;
  const results = await Promise.allSettled([loadOrgs(), loadTokens()]);
  busy.value = false;
  const errors = results
    .filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    )
    .map(result => getMessage(result.reason))
    .join('\n');
  if (errors) {
    console.log(errors);
    emit('error', errors);
  }
  resetEditedItem();
}

defineExpose({ load });

onMounted(load);

watch(
  dialog,
  (value) => {
    if (!value) {
      nextTick(resetEditedItem);
    }
  },
  { immediate: true },
);

watch(dialogDelete, (value) => {
  if (!value) {
    nextTick(resetEditedItem);
  }
});

watch(dialogCreated, (value) => {
  if (!value) {
    nextTick(() => {
      createdToken.value.name = '';
      createdToken.value.value = '';
    });
  }
});

async function save() {
  if (!props.dicomArchive) return;
  if (!dialogValid.value) return;
  if (!editedItem.value) return;

  try {
    if (!editedItem.value.name) {
      emit('error', 'Token name is required');
      return;
    }
    const { data, error } = await supabase.rpc('orthanc_create_upload_token', {
      server_id: props.dicomArchive,
      sub: editedItem.value.name,
      org: editedItem.value.org,
      validity_duration_seconds: editedItem.value.validitySeconds,
    });
    if (error) throw error;
    createdToken.value = {
      name: editedItem.value.name,
      value: data,
    };
    dialogCreated.value = true;
    load();
  }
  catch (error) {
    console.error(error);
    emit('error', getMessage(error));
  }
  finally {
    dialog.value = false;
  }
}

function deleteItem(item: Row) {
  editedIndex.value = items.value.indexOf(item);
  editedItem.value = editedItemFromRow(item);
  dialogDelete.value = true;
}

async function deleteItemConfirm() {
  if (!editedItem.value?.id) return;
  try {
    const { count, error } = await supabase
      .from('token')
      .delete({ count: 'exact' })
      .eq('id', editedItem.value.id);
    if (error) throw error;
    if (count === 0) throw new Error('Could not delete token');
    load();
  }
  catch (error) {
    console.error(error);
    emit('error', getMessage(error));
  }
  finally {
    dialogDelete.value = false;
  }
}
</script>

<template>
  <v-data-table
    items-per-page="5"
    :items-per-page-options="itemsPerPageOptions"
    :items="items"
    :headers="headers"
    :loading="busy"
  >
    <template #top>
      <v-toolbar flat>
        <v-dialog v-model="dialog" max-width="50em">
          <template #activator="{ props: activatorProps }">
            <v-btn color="primary" v-bind="activatorProps" prepend-icon="mdi-plus">
              Add
            </v-btn>
            <v-spacer />
          </template>
          <v-card>
            <v-card-title>
              <span class="text-headline-small">{{ dialogTitle }}</span>
            </v-card-title>

            <v-form
              v-model="dialogValid"
              validate-on="invalid-input"
              @submit.prevent="save"
            >
              <v-card-text>
                <v-container v-if="editedItem">
                  <v-row>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.name"
                        :rules="[rules.required]"
                        label="Token Name"
                      />
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                      <v-autocomplete
                        v-model="editedItem.org"
                        :items="orgAutocompleteItems"
                        label="Organization"
                        :rules="[rules.required]"
                      />
                    </v-col>

                    <v-col cols="12" md="6" sm="12">
                      <v-autocomplete
                        v-model="editedItem.validitySeconds"
                        label="Validity"
                        :items="validityAutocompleteItems"
                        :rules="[rules.required]"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  @click="dialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  type="submit"
                  :disabled="dialogValid === false"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="dialogCreated"
          max-width="50em"
          @keydown.esc="dialogCreated = false"
        >
          <v-card>
            <v-card-title class="text-title-large text-center">
              Token created
            </v-card-title>
            <v-card-text>
              <div class="text-title-large text-center mb-2">
                Your token
                <span class="text-primary">{{ createdToken.name }}</span> has
                been created:
              </div>
              <v-textarea
                v-model="createdToken.value"
                density="compact"
                readonly
              >
                <template #append>
                  <UseClipboard
                    v-slot="{ copy, copied }"
                    :source="createdToken.value"
                  >
                    <v-icon class="me-2" size="small" @click="copy()">
                      {{ copied ? 'mdi-check ' : 'mdi-content-copy' }}
                    </v-icon>
                  </UseClipboard>
                </template>
              </v-textarea>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="dialogCreated = false"
              >
                OK
              </v-btn>
              <v-spacer />
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="dialogDelete"
          max-width="50em"
          @keydown.esc="dialogDelete = false"
        >
          <v-card>
            <v-card-title class="text-title-large text-center">
              Confirmation required
            </v-card-title>
            <v-card-text>
              <div class="text-title-large text-center mb-2">
                Really remove STOW-RS token
                <span class="text-primary">{{ editedItem?.name }}</span>?
              </div>

              This action <em>cannot</em> be undone and will
              <em>revoke</em> STOW-RS access with this token.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="dialogDelete = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="deleteItemConfirm"
              >
                OK
              </v-btn>
              <v-spacer />
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn
          :disabled="busy"
          icon="mdi-refresh"
          aria-label="Refresh"
          color="primary"
          @click="load"
        />
      </v-toolbar>
    </template>
    <template #item.payload.exp="{ item }">
      {{ new Date(item.payload.exp * 1000).toLocaleString() }}
    </template>
    <template #item.token="{ item }">
      <span v-if="revealedTokens.includes(item.id)" style="user-select: text">{{
        item.token
      }}</span>
      <v-btn v-else @click="revealedTokens.push(item.id)">
        Reveal
      </v-btn>
    </template>
    <template #item.actions="{ item }">
      <UseClipboard v-slot="{ copy, copied }" :source="item.token">
        <v-icon class="me-2" size="small" @click="copy()">
          {{ copied ? 'mdi-check ' : 'mdi-content-copy' }}
        </v-icon>
      </UseClipboard>
      <v-icon size="small" @click="deleteItem(item)">
        mdi-delete
      </v-icon>
    </template>
  </v-data-table>
</template>

<style scoped></style>
